import { Loader2, UserCog } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from '@/components/layout/sidebar';
import { Topbar } from '@/components/layout/topbar';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';

function ImpersonationBanner() {
    const { user, impersonatorName, stopImpersonating } = useAuth();

    return (
        <div className="flex flex-wrap items-center justify-between gap-2 bg-amber-500 px-3 py-1.5 text-xs font-semibold text-amber-950 sm:gap-3 sm:px-4">
            <span className="flex min-w-0 items-center gap-1.5">
                <UserCog className="size-3.5 shrink-0" />
                <span className="min-w-0">
                    {impersonatorName ?? 'An admin'} is viewing as {user?.name} — actions taken now affect this account.
                </span>
            </span>
            <Button
                size="sm"
                variant="outline"
                className="h-6 shrink-0 border-amber-950/30 bg-transparent px-2 text-[11px] hover:bg-amber-600/20"
                onClick={() => void stopImpersonating()}
            >
                Return to my account
            </Button>
        </div>
    );
}

export function AppLayout() {
    const { isAuthenticated, isLoading, impersonating } = useAuth();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setMobileMenuOpen(false);
    }, [location.pathname]);

    if (isLoading) {
        return (
            <div className="flex h-screen items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" /> Loading…
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return (
        <div className="flex h-screen flex-col overflow-hidden bg-background">
            {impersonating && <ImpersonationBanner />}
            <div className="flex flex-1 overflow-hidden">
                <Sidebar mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />
                <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                    <Topbar onMenuClick={() => setMobileMenuOpen(true)} />
                    <main className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-6">
                        <Outlet />
                    </main>
                </div>
            </div>
        </div>
    );
}
